import { Buffer } from 'buffer';
import { useCallback, useMemo } from 'react';
import _, { isEmpty } from 'lodash';
import { EProvisioning, ERole, ERollout } from '@lumahealthhq/ui-model-types';
import { useConfig, useLogger } from '@lumahealthhq/ui-utils';
import useCurrentMaster from './useCurrentMaster';
import useCurrentToken from './useCurrentToken';
import useCurrentUser from './useCurrentUser';
export let EUsePermissionsMode;
(function (EUsePermissionsMode) {
  EUsePermissionsMode[EUsePermissionsMode["Some"] = 0] = "Some";
  EUsePermissionsMode[EUsePermissionsMode["Every"] = 1] = "Every";
})(EUsePermissionsMode || (EUsePermissionsMode = {}));
function hasAccessToProductUtil(permissions, value, arrayMode = EUsePermissionsMode.Some) {
  // Use recursivity for arrays
  if (_.isArray(value)) {
    if (_.isEmpty(value)) {
      return true;
    }
    const fn = arrayMode === EUsePermissionsMode.Some ? _.some : _.every;
    return fn(value, k => hasAccessToProductUtil(permissions, k));
  }
  if (!value) {
    return true;
  }
  if (!permissions) {
    return false;
  }
  return _.includes(permissions.allProducts, value);
}
function currentMasterReseller(config, currentMaster) {
  var _config$resellerPartn;
  if (!config) {
    return null;
  }

  // When the User is a partner, it will not have the reseller on the salesforceData, but the master will be the reseller
  const masterResellerFromPartnerUser = (_config$resellerPartn = config.resellerPartners) == null ? void 0 : _config$resellerPartn.find(reseller => {
    var _reseller$maaRootAcco;
    return (_reseller$maaRootAcco = reseller.maaRootAccounts) == null ? void 0 : _reseller$maaRootAcco.includes(_.get(currentMaster, '_id', ''));
  });
  if (masterResellerFromPartnerUser) {
    return masterResellerFromPartnerUser;
  }

  // customers that belong to a reseller will have the reseller on the salesforceData
  const currentMasterResellerID = _.get(currentMaster, 'salesforceData.reseller', '');
  if (!currentMasterResellerID) {
    return null;
  }
  return config.resellerPartners.find(reseller => reseller.reseller === currentMasterResellerID) || null;
}
export default function usePermissions() {
  const logger = useLogger();
  const config = useConfig();
  const currentUser = useCurrentUser({
    ignoreError: true
  });
  const currentMaster = useCurrentMaster({
    ignoreError: true
  });
  const [currentToken] = useCurrentToken();
  const tokenInfo = useMemo(() => {
    if (currentToken) {
      try {
        // TODO wait for BE to add the "isResellerPartner" inside the token
        const {
          isLumaAdmin,
          adminUser
        } = JSON.parse(Buffer.from(currentToken.split('.')[1], 'base64').toString());
        return {
          isLumaAdmin,
          adminUser
        };
      } catch (error) {
        logger.error('Cannot parse token to get token info for usePermissions', {
          error
        });
      }
    }
    return {
      isLumaAdmin: false,
      adminUser: false
    };
  }, [currentToken, logger]);
  const permissions = useMemo(() => {
    var _currentUser$imperson;
    if (!currentUser) {
      return undefined;
    }
    const isAdmin = _.includes(currentUser.roles, ERole.Admin);
    const isManager = _.includes(currentUser.roles, ERole.Manager);
    const isSubaccount = _.includes(currentUser.roles, ERole.Subaccount);
    // @ts-ignore
    const isMaster = currentUser._id === currentUser.user;
    const isLumaUser = _.includes(currentUser.email, '@lumahealth.io');
    const isImpersonatedByAdmin = !!tokenInfo.adminUser;
    const isImpersonatedByLumaAdmin = !!tokenInfo.isLumaAdmin;
    const masterReseller = currentMasterReseller(config, currentMaster || {});
    const isDemoAccount = /demo\+[\w]+@lumahealth\.io/.test(currentUser.email || '');
    const respectProvisioning = _.get(currentMaster, 'salesforceData.respectProvisioning', false);
    let provisioning;
    if (!respectProvisioning) {
      // if respectProvisioning is `false` give access to all provisioning.
      provisioning = _.values(EProvisioning);
    } else {
      // if respectProvisoning is `true`:
      provisioning = _.get(currentUser, 'salesforceData.provisioning', []);
      // and its not the master accounts you get the intersetion between the current user provisoning and the master
      if (!isMaster) {
        const provisioningMaster = _.get(currentMaster, 'salesforceData.provisioning', []);
        provisioning = _.intersection(provisioning, provisioningMaster);
      }
    }
    // if its a provider we ignore the respectProvisioning and always use the current providers provisioning
    if (currentUser.type === 'doctor') {
      const provisioningMaster = _.get(currentMaster, 'salesforceData.provisioning', []);
      // Only add chat if respect provisioning is off OR the master has chat provisioning.
      const chatProvisioning = respectProvisioning ? provisioningMaster.filter(permission => permission === EProvisioning.Chat) : [EProvisioning.Chat];
      provisioning = [..._.get(currentUser, 'salesforceData.provisioning', []), ...chatProvisioning];
    }

    // NOTE: uncomment to enable all provisioning:
    // provisioning = values(EProvisioning);

    // NOTE: uncomment to enable all rollouts:
    // const rollouts = values(RolloutEnum);
    const rollouts = _.get(currentMaster, 'rollouts', []);
    return {
      allProducts: _.concat(provisioning, rollouts),
      // @ts-ignore
      hasAccessAdminPage: !!(currentUser != null && (_currentUser$imperson = currentUser.impersonate) != null && _currentUser$imperson.length),
      hasAccessToLabs: _.get(currentUser, 'settings.labs.enabled'),
      hasAccessBroadcastApprove: isAdmin || isManager || isMaster,
      isAdmin,
      isDemoAccount,
      isManager,
      isLumaUser,
      isMaster,
      isSubaccount,
      provisioning,
      respectProvisioning,
      rollouts,
      hasAccessWaitlistsTags: isDemoAccount || _.includes(rollouts, ERollout.WaitlistsTags),
      masterReseller,
      isImpersonatedByAdmin,
      isImpersonatedByLumaAdmin,
      isMasterResellerPartner: !!masterReseller
    };
  }, [currentUser, tokenInfo.isLumaAdmin, tokenInfo.adminUser, currentMaster, config]);

  /**
   * Check if the current user has access to a product/s
   *
   * Note that users with `salesforceData.respectProvisioning = false` will have
   * access to all provisioning available. This doesn't apply for rollouts.
   *
   * @param product provisioning, rollout, or a list of products
   * @param arrayMode logic to apply in case we pass an array with different values
   *
   * @example
   * // Check if the user has access to Chat & ExternalID:
   * const { hasAccessToProduct } = usePermissions();
   * hasAccessToProduct([EProvisioning.Chat, ERollout.ExternalId], 'every');
   */
  const hasAccessToProduct = useCallback((product, mode) => {
    if (isEmpty(product)) {
      return true;
    }
    if (!permissions) {
      logger.warn('Cannot run "hasAccessToProduct" since permissions have not been loaded yet', {
        product
      });
      return false;
    }
    return hasAccessToProductUtil(permissions, product, mode);
  }, [permissions, logger]);
  return {
    permissions,
    hasAccessToProduct
  };
}